import { useEffect, useState } from 'react'
import { Alert, Box, Chip, IconButton, Paper, Skeleton, Tooltip, Typography } from '@mui/material'
import {
  Article as WordIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  OpenInNew as OpenIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material'
import { Attachment, attachmentsApi } from '../../services/api'
import { useNotification } from '../../hooks/useNotification'
import { formatFileSize, isPdf, isWordDocument } from '../../utils/resumeFile'

interface ResumeFilePreviewProps {
  attachment: Attachment
  /** Extra controls beside Open and Download (the staff panel's Replace and Remove). */
  actions?: React.ReactNode
}

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

// An uploaded resume as the client and staff both see it. A PDF previews in the
// page; a Word document cannot render in a browser, so it offers the download
// and says why (MWA-272).
export default function ResumeFilePreview({ attachment, actions }: ResumeFilePreviewProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewFailed, setPreviewFailed] = useState(false)
  const { showError } = useNotification()
  const pdf = isPdf(attachment.contentType)
  const word = isWordDocument(attachment.contentType)

  useEffect(() => {
    setPreviewUrl(null)
    setPreviewFailed(false)
    if (!pdf) return
    let cancelled = false
    attachmentsApi
      .generateDownloadUrl(attachment.id, 'preview')
      .then(({ downloadUrl }) => {
        if (!cancelled) setPreviewUrl(downloadUrl)
      })
      .catch((err) => {
        console.error('Failed to load the resume preview:', err)
        if (!cancelled) setPreviewFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [attachment.id, pdf])

  const handleDownload = async () => {
    try {
      await attachmentsApi.download(attachment.id)
    } catch (err) {
      console.error('Failed to download the resume:', err)
      showError('Failed to download the resume')
    }
  }

  const handleOpen = async () => {
    try {
      await attachmentsApi.preview(attachment.id)
    } catch (err) {
      console.error('Failed to open the resume:', err)
      showError('Failed to open the resume')
    }
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, gap: 1.5 }}>
          {pdf ? <PdfIcon color="primary" /> : word ? <WordIcon color="primary" /> : <FileIcon color="primary" />}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
                {attachment.fileName}
              </Typography>
              <Chip
                label={formatFileSize(attachment.fileSize)}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Uploaded {attachment.uploadedByName ? `by ${attachment.uploadedByName} ` : ''}on{' '}
              {formatDate(attachment.createdAt)}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {actions}
          {pdf && (
            <Tooltip title="Open in new tab">
              <IconButton size="small" onClick={handleOpen} color="primary" aria-label="Open resume in new tab">
                <OpenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Download">
            <IconButton size="small" onClick={handleDownload} aria-label="Download resume">
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {pdf && previewUrl && (
        <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 1 }}>
          <iframe
            src={previewUrl}
            title="Resume preview"
            // A native PDF viewer cannot report its content height, so this caps at
            // a sensible height and lets the viewer's own scrollbar take over.
            style={{ width: '100%', height: 'clamp(480px, 65vh, 820px)', border: 'none' }}
          />
        </Paper>
      )}
      {pdf && !previewUrl && !previewFailed && <Skeleton variant="rounded" height={480} />}
      {pdf && previewFailed && (
        <Alert severity="warning">The preview did not load. Download the file to read it.</Alert>
      )}
      {!pdf && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <WordIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary">
            Word documents open in Word, not in the browser. Download the file to read it.
          </Typography>
        </Paper>
      )}
    </Box>
  )
}
